import React, { useEffect, useRef, useState } from "react";

import classNames from "classnames";

import MessageWrapper from "./MessageWrapper";
import CustomMessage from "./CustomMessage";
import ChatRepliedMessage from "./ChatRepliedMessage";

import { ActiveSharedBetSlip, ChatMessageType } from "@data-types/chat";

import { downloadFile } from "@utils/chat";
import { FileRepository } from "@amityco/ts-sdk";
import { ChatSDKService } from "@services/chat";
import { observer } from "mobx-react";
import { ChatViewStore } from "@state/stores/chat/ChatViewStore";
import { useRootAppStore } from "@hooks";

export default observer(function MessageContainerHandler(props: {
	support?: boolean;
	store?: ChatViewStore;
	message: Amity.Message &
		Amity.RawMessage &
		any & {
			parentId?: string;
			data: {
				text?: string;
				sharedSlip: ActiveSharedBetSlip;
				messageType: ChatMessageType.REPLY;
				replyingCreatorId: string;
				replyingMessage: string;
				betSlipData: ActiveSharedBetSlip;
			};
			metadata: {
				isLink?: boolean;
				isMultiOffer?: boolean;
			};
		};
}) {
	const messageData = props.message;
	const rootStore = useRootAppStore();
	const isUserCreator =
		props.message.creatorId === rootStore.userAuthStore.user?.chatName;

	const msgClasses = classNames("chat__message", {
		"chat--support__message": props.support,
		"chat--support__message--right": props.support && isUserCreator,
		"chat--support__message--left": props.support && !isUserCreator,
	});

	if (
		messageData.dataType == ChatMessageType.IMAGE ||
		messageData.dataType == ChatMessageType.FILE
	) {
		return (
			<FileMessage
				store={props.store}
				isUserCreator={isUserCreator}
				messageData={messageData}
				msgClasses={msgClasses}
				support={props.support}
			/>
		);
	}

	if (messageData.data.messageType != ChatMessageType.REPLY) {
		if (messageData.data.sharedSlip == null) {
			return (
				<MessageWrapper
					isLink={messageData.metadata?.isLink}
					anchor={messageData.data.text}
				>
					<div className={msgClasses}>{messageData.data.text}</div>
				</MessageWrapper>
			);
		} else {
			return (
				<CustomMessage
					customMessageData={messageData}
					isLink={messageData.metadata?.isLink}
					isMultiOffer={messageData.metadata?.isMultiOffer}
				/>
			);
		}
	}

	if (messageData.data.messageType == ChatMessageType.REPLY) {
		return <ChatRepliedMessage message={messageData} />;
	}

	return <></>;
});

const FileMessage = observer(
	({
		messageData,
		isUserCreator,
		support,
		msgClasses,
		store,
	}: {
		messageData: any;
		store?: ChatViewStore;
		isUserCreator: boolean;
		support?: boolean;
		msgClasses: string;
	}) => {
		const mediaClasses = classNames("chat--support__media", {
			"chat--support__media--right": support && isUserCreator,
			"chat--support__media--left": support && !isUserCreator,
		});

		const imgClasses = classNames("chat--support__img", {
			"chat--support__img--right": isUserCreator,
			"chat--support__img--left": !isUserCreator,
		});

		const fileClasses = classNames("card--file", {
			"chat--support__file": support,
			"chat--support__file--right": support && isUserCreator,
			"chat--support__file--left": support && !isUserCreator,
		});

		const [fileResult, setFileResult] =
			useState<Amity.Snapshot<Amity.File>>();
		const { data: fileData } = fileResult ?? {};

		useEffect(() => {
			if (messageData.data?.fileId) {
				FileRepository.observeFile(
					messageData.data.fileId,
					setFileResult
				);
			}
		}, [messageData.data?.fileId]);

		if (messageData.dataType == ChatMessageType.IMAGE) {
			const mediumImage = fileData?.fileUrl
				? ChatSDKService.getImageThumbnail(fileData?.fileUrl, "medium")
				: undefined;

			return (
				<>
					<MessageWrapper
						isLink={messageData.metadata?.isLink}
						anchor={messageData.data?.text}
					>
						<div
							className={classNames(msgClasses, {
								"chat--support__message--media": support,
							})}
						>
							{messageData.data?.caption}
						</div>
					</MessageWrapper>

					<div className={mediaClasses}>
						<img
							onLoad={() => store?.setImagesDoneLoading(true)}
							src={mediumImage}
							alt="No image"
							className={imgClasses}
						/>
					</div>
				</>
			);
		}

		if (messageData.dataType == ChatMessageType.FILE) {
			return (
				<>
					<MessageWrapper
						isLink={messageData.metadata?.isLink}
						anchor={messageData.data?.text}
					>
						<div
							className={classNames(msgClasses, {
								"chat--support__message--media": support,
							})}
						>
							{messageData.data?.caption}
						</div>
					</MessageWrapper>
					<div
						onClick={() => {
							if (
								fileData?.attributes.name &&
								fileData?.fileUrl
							) {
								downloadFile(
									fileData?.attributes.name,
									fileData?.fileUrl
								);
							}
						}}
						className={mediaClasses}
					>
						<div className={fileClasses}>
							<i
								className={classNames(
									"u-icon u-icon--xlrg u-icon--file",
									[
										`u-icon--file--${fileData?.attributes?.extension}`,
									]
								)}
							/>

							<div className="card--file__info u-display--f--grow">
								<div className="card--file__name u-type--left">
									{fileData?.attributes?.name}
								</div>
								<div className="card--file__size u-type--left">
									{fileData?.attributes?.size} KB
								</div>
							</div>
						</div>
					</div>
				</>
			);
		}

		return null;
	}
);
